// Server copy of the page (injected by the Netlify edge function inside #root, see backend/seo_body.py).
//
// Before React mounts, the copy is moved out of #root (React's createRoot would otherwise wipe it on
// the first commit and show an empty loading screen). The copy stays on screen while React builds the
// real page invisibly on top of it (#root.ssr-pending); as soon as the real page has its heading,
// nothing is still loading and its eager (above-the-fold) images are decoded, the copy is removed
// and #root becomes visible — one swap, same layout, no jump.
const READY_TIMEOUT = 12000;

function pendingImages(root) {
  const main = root.querySelector('main');
  if (!main) return [];
  const view = root.ownerDocument && root.ownerDocument.defaultView;
  const limit = ((view && view.innerHeight) || 900) * 1.2;
  return [...main.querySelectorAll('img')].filter(img => img.getAttribute('loading') !== 'lazy' && !img.complete
    && img.getBoundingClientRect().top < limit);
}

export function isPageReady(root) {
  if (!root) return false;
  if (root.querySelector('[data-testid="storefront-error"]')) return true;
  const main = root.querySelector('main');
  if (!main || !main.querySelector('h1')) return false;
  if (main.querySelector('[role="status"], [aria-busy="true"]')) return false;
  return pendingImages(root).length === 0;
}

export function adoptServerContent(doc = typeof document !== 'undefined' ? document : null, {timeout = READY_TIMEOUT} = {}) {
  const root = doc && doc.getElementById('root');
  const copy = root && root.querySelector(':scope > [data-ssr]');
  if (!copy) return null;
  root.after(copy);
  root.classList.add('ssr-pending');
  let done = false;
  let observer = null;
  let timer = null;
  const watched = new WeakSet();
  const finish = () => {
    if (done) return;
    done = true;
    if (observer) observer.disconnect();
    if (timer) clearTimeout(timer);
    copy.remove();
    root.classList.remove('ssr-pending');
  };
  const check = () => {
    if (done) return;
    if (isPageReady(root)) {
      finish();
      return;
    }
    // Image loads do not mutate the DOM: re-check when the pending ones finish (or fail).
    for (const img of pendingImages(root)) {
      if (watched.has(img)) continue;
      watched.add(img);
      img.addEventListener('load', check, {once: true});
      img.addEventListener('error', check, {once: true});
    }
  };
  const View = doc.defaultView || {};
  if (View.MutationObserver) {
    observer = new View.MutationObserver(check);
    observer.observe(root, {childList: true, subtree: true, attributes: true, attributeFilter: ['role', 'aria-busy', 'src']});
  }
  timer = setTimeout(finish, timeout);
  return {finish, check, copy};
}
